import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  TextField,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Chip,
  Paper,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import {
  useTestCreateNotificationMutation,
  useTestAllNotificationsMutation,
  useGetUserNotificationsQuery,
  useGetUnreadCountQuery,
  useMarkNotificationsAsReadMutation,
  useMarkNotificationsAsUnreadMutation,
  useMarkAllNotificationsAsReadMutation,
  useDeleteNotificationsMutation,
  useUpdatePushTokenMutation,
} from '../../redux/adminApiSlice';

// Add route status type
interface RouteStatus {
  method: string;
  path: string;
  description: string;
  status: 'working' | 'testing' | 'error';
  endpoint: string;
}

export default function NotificationsTab() {
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [pushToken, setPushToken] = useState('');
  const [selectedNotifications, setSelectedNotifications] = useState<string[]>([]);

  // API hooks
  const [testCreate, { isLoading: isCreating, isError: isCreateError }] = useTestCreateNotificationMutation();
  const [testAll, { isLoading: isTestingAll, isError: isTestAllError }] = useTestAllNotificationsMutation();
  const { 
    data: notifications, 
    isLoading: isLoadingNotifications, 
    isError: isNotificationsError,
    refetch: refetchNotifications 
  } = useGetUserNotificationsQuery({ page, limit });
  const { 
    data: unreadCount, 
    isError: isUnreadCountError,
    refetch: refetchUnreadCount 
  } = useGetUnreadCountQuery();
  const [markAsRead, { isError: isMarkAsReadError }] = useMarkNotificationsAsReadMutation();
  const [markAllAsRead, { isError: isMarkAllReadError }] = useMarkAllNotificationsAsReadMutation();
  const [deleteNotifications, { isError: isDeleteError }] = useDeleteNotificationsMutation();
  const [updateToken, { isLoading: isUpdatingToken, isError: isUpdateTokenError }] = useUpdatePushTokenMutation();
  const [markAsUnread] = useMarkNotificationsAsUnreadMutation();

  // Define routes and their status
  const routes: RouteStatus[] = [
    {
      method: 'POST',
      path: '/api/notifications/test-all',
      description: 'Test all notification features',
      status: isTestAllError ? 'error' : 'working',
      endpoint: 'testAll'
    },
    {
      method: 'POST',
      path: '/api/notifications/test',
      description: 'Create a test notification',
      status: isCreateError ? 'error' : 'working',
      endpoint: 'testCreate'
    },
    {
      method: 'GET',
      path: '/api/notifications',
      description: 'Get user\'s notifications',
      status: isNotificationsError ? 'error' : 'working',
      endpoint: 'getNotifications'
    },
    {
      method: 'POST',
      path: '/api/notifications/mark-read',
      description: 'Mark notifications as read',
      status: isMarkAsReadError ? 'error' : 'working',
      endpoint: 'markAsRead'
    },
    {
      method: 'POST',
      path: '/api/notifications/mark-unread',
      description: 'Mark notifications as unread',
      status: 'working',
      endpoint: 'markAsUnread'
    },
    {
      method: 'POST',
      path: '/api/notifications/mark-all-read',
      description: 'Mark all notifications as read',
      status: isMarkAllReadError ? 'error' : 'working',
      endpoint: 'markAllRead'
    },
    {
      method: 'DELETE',
      path: '/api/notifications/delete',
      description: 'Delete notifications',
      status: isDeleteError ? 'error' : 'working',
      endpoint: 'delete'
    },
    {
      method: 'GET',
      path: '/api/notifications/unread-count',
      description: 'Get unread notification count',
      status: isUnreadCountError ? 'error' : 'working',
      endpoint: 'unreadCount'
    },
    {
      method: 'POST',
      path: '/api/notifications/push-token',
      description: 'Update push notification token',
      status: isUpdateTokenError ? 'error' : 'working',
      endpoint: 'pushToken'
    }
  ];

  const getStatusColor = (status: RouteStatus['status']) => {
    switch (status) {
      case 'working':
        return '#4CAF50' as const; // Bright green
      case 'testing':
        return '#FFC107' as const; // Bright yellow/amber
      case 'error':
        return '#FF5252' as const; // Bright red
      default:
        return '#9E9E9E' as const; // Light grey
    }
  };

  // Handlers
  const handleTestCreate = async () => {
    try {
      const result = await testCreate().unwrap();
      refetchNotifications();
      refetchUnreadCount();
      console.log('Test notification created:', result);
    } catch (error) {
      console.error('Error creating test notification:', error);
    }
  };

  const handleTestAll = async () => {
    try {
      const result = await testAll().unwrap();
      refetchNotifications();
      refetchUnreadCount();
      console.log('All notification features tested:', result);
    } catch (error) {
      console.error('Error testing all features:', error);
    }
  };

  const handleMarkAsRead = async () => {
    if (selectedNotifications.length === 0) return;
    try {
      await markAsRead(selectedNotifications).unwrap();
      setSelectedNotifications([]);
      refetchNotifications();
      refetchUnreadCount();
    } catch (error) {
      console.error('Error marking notifications as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsRead().unwrap();
      refetchNotifications();
      refetchUnreadCount();
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const handleDelete = async () => {
    if (selectedNotifications.length === 0) return;
    try {
      await deleteNotifications(selectedNotifications).unwrap();
      setSelectedNotifications([]);
      refetchNotifications();
      refetchUnreadCount();
    } catch (error) {
      console.error('Error deleting notifications:', error);
    }
  };

  const handleUpdateToken = async () => {
    if (!pushToken) return;
    try {
      await updateToken({ token: pushToken }).unwrap();
      setPushToken('');
    } catch (error) {
      console.error('Error updating push token:', error);
    }
  };

  const handleMarkAsUnread = async () => {
    if (selectedNotifications.length === 0) return;
    try {
      await markAsUnread(selectedNotifications).unwrap();
      setSelectedNotifications([]);
      refetchNotifications();
      refetchUnreadCount();
    } catch (error) {
      console.error('Error marking notifications as unread:', error);
    }
  };

  const toggleNotificationSelection = (id: string) => {
    setSelectedNotifications(prev => 
      prev.includes(id) ? prev.filter(nId => nId !== id) : [...prev, id]
    );
  };

  return (
    <Box>
      <Grid container spacing={3}>
        {/* Control Panel */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Test Controls
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Button
                  variant="contained"
                  onClick={handleTestCreate}
                  disabled={isCreating}
                >
                  {isCreating ? <CircularProgress size={24} /> : 'Create Test Notification'}
                </Button>
                <Button
                  variant="contained"
                  onClick={handleTestAll}
                  disabled={isTestingAll}
                >
                  {isTestingAll ? <CircularProgress size={24} /> : 'Test All Features'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleMarkAllAsRead}
                  disabled={!notifications?.notifications?.length}
                >
                  Mark All as Read
                </Button>
                <Box sx={{ mt: 2 }}>
                  <TextField
                    fullWidth
                    label="Push Notification Token"
                    value={pushToken}
                    onChange={(e) => setPushToken(e.target.value)}
                    margin="normal"
                  />
                  <Button
                    variant="contained"
                    onClick={handleUpdateToken}
                    disabled={!pushToken || isUpdatingToken}
                    sx={{ mt: 1 }}
                  >
                    Update Token
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Notifications List */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Notifications
                  {unreadCount?.unreadCount !== undefined && (
                    <Chip
                      label={`${unreadCount.unreadCount} unread`}
                      color="primary"
                      size="small"
                      sx={{ ml: 1 }}
                    />
                  )}
                </Typography>
                <Box>
                  <Button
                    variant="outlined"
                    onClick={handleMarkAsRead}
                    disabled={selectedNotifications.length === 0}
                    sx={{ mr: 1 }}
                  >
                    Mark as Read
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleMarkAsUnread}
                    disabled={selectedNotifications.length === 0}
                    sx={{ mr: 1 }}
                  >
                    Mark as Unread
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={handleDelete}
                    disabled={selectedNotifications.length === 0}
                  >
                    Delete Selected
                  </Button>
                </Box>
              </Box>

              {isLoadingNotifications ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : notifications?.notifications?.length ? (
                <>
                  <List>
                    {notifications.notifications.map((notification) => (
                      <ListItem
                        key={notification._id}
                        sx={{
                          backgroundColor: notification.read ? 'transparent' : 'rgba(25, 118, 210, 0.08)',
                          borderRadius: 1,
                          mb: 1
                        }}
                        secondaryAction={
                          <Box>
                            <IconButton
                              edge="end"
                              onClick={() => toggleNotificationSelection(notification._id)}
                              color={selectedNotifications.includes(notification._id) ? 'primary' : 'default'}
                            >
                              <CheckCircleIcon />
                            </IconButton>
                            <IconButton
                              edge="end"
                              onClick={() => deleteNotifications([notification._id])}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        }
                      >
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <FiberManualRecordIcon 
                                sx={{ 
                                  mr: 1, 
                                  fontSize: 12,
                                  color: notification.read ? 'transparent' : '#1976d2',
                                  visibility: notification.read ? 'hidden' : 'visible'
                                }}
                              />
                              {notification.content}
                            </Box>
                          }
                          secondary={
                            <>
                              <Typography component="span" variant="body2" color="text.primary">
                                {notification.type}
                              </Typography>
                              {` - ${new Date(notification.createdAt).toLocaleString()}`}
                            </>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Button 
                      disabled={page === 1} 
                      onClick={() => setPage(p => p - 1)}
                    >
                      Previous
                    </Button>
                    <Typography sx={{ mx: 2, alignSelf: 'center' }}>
                      Page {page}
                    </Typography>
                    <Button 
                      disabled={!notifications?.notifications?.length || notifications.notifications.length < limit}
                      onClick={() => setPage(p => p + 1)}
                    >
                      Next
                    </Button>
                  </Box>
                </>
              ) : (
                <Alert severity="info">No notifications found</Alert>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Route Status Section */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            API Routes Status
          </Typography>
          <Grid container spacing={2}>
            {routes.map((route, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Paper 
                  elevation={2}
                  sx={{
                    p: 2,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    '&:hover': {
                      boxShadow: 3
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <FiberManualRecordIcon 
                      sx={{ 
                        mr: 1, 
                        fontSize: 16,
                        color: getStatusColor(route.status)
                      }}
                    />
                    <Chip 
                      label={route.method}
                      size="small"
                      sx={{ mr: 1 }}
                    />
                  </Box>
                  <Typography variant="body2" sx={{ mb: 1, wordBreak: 'break-all' }}>
                    {route.path}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {route.description}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
} 